/**
 * 生PCM WAV（GOKURI 録音は 11.025kHz / 16bit / mono 固定）から、波形描画用の
 * ダウンサンプル済みピーク包絡を **一定メモリ** で算出するユーティリティ。
 *
 * 全量を AudioBuffer にデコードせず、ストリーム取得しながら int16 サンプルを
 * バケツ詰め（バケツごとの最大絶対値）するため、任意長でもメモリは
 * `targetPeaks` 個の Float32 に収まる。算出したピークは WaveSurfer の
 * `load(url, [peaks], durationSec)` にそのまま渡せる（0..1 に正規化済み）。
 *
 * duration は WAV ヘッダ（data チャンクサイズ ÷ byteRate）由来の実値を返すため、
 * wavDuration メタデータに依存しない。
 */

export interface WavFormatInfo {
  sampleRate: number;
  bitsPerSample: number;
  channels: number;
  /** data チャンク本体の先頭オフセット（バイト） */
  dataOffset: number;
  /** data チャンクのバイト長 */
  dataSize: number;
}

export interface WavPeaksResult {
  /** 0..1 に正規化されたピーク包絡（長さ <= targetPeaks） */
  peaks: Float32Array;
  /** WAV ヘッダ由来の実 duration（秒） */
  durationSec: number;
  sampleRate: number;
}

export interface StreamWavPeaksOptions {
  contentLength?: number;
  targetPeaks?: number;
  signal?: AbortSignal;
  onProgress?: (percent: number | null) => void;
}

const HEADER_SCAN_LIMIT_BYTES = 64 * 1024;
const DEFAULT_TARGET_PEAKS = 8000;
const INT16_FULL_SCALE = 32768;

function readTag(buf: Uint8Array, offset: number): string {
  return String.fromCharCode(
    buf[offset],
    buf[offset + 1],
    buf[offset + 2],
    buf[offset + 3],
  );
}

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/**
 * RIFF/WAVE のチャンクを走査し fmt と data を特定する。
 * - 解析できれば `WavFormatInfo`
 * - まだバイトが足りなければ `'incomplete'`（呼び出し側が追加読み込み）
 * - WAV でない/未対応なら `null`
 */
export function parseWavHeader(
  buf: Uint8Array,
): WavFormatInfo | 'incomplete' | null {
  if (buf.length < 12) return 'incomplete';
  const riff = readTag(buf, 0);
  if (riff !== 'RIFF' && riff !== 'RF64') return null;
  if (readTag(buf, 8) !== 'WAVE') return null;

  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let offset = 12;
  let fmt: Pick<WavFormatInfo, 'sampleRate' | 'bitsPerSample' | 'channels'> | null =
    null;

  while (offset + 8 <= buf.length) {
    const id = readTag(buf, offset);
    const size = dv.getUint32(offset + 4, true);
    const body = offset + 8;
    if (id === 'fmt ') {
      if (body + 16 > buf.length) return 'incomplete';
      const channels = dv.getUint16(body + 2, true);
      const sampleRate = dv.getUint32(body + 4, true);
      const bitsPerSample = dv.getUint16(body + 14, true);
      fmt = { sampleRate, bitsPerSample, channels };
    } else if (id === 'data') {
      if (!fmt) return null; // fmt より前に data が来る WAV は未対応
      return { ...fmt, dataOffset: body, dataSize: size };
    }
    // チャンクは 2 バイト境界（奇数サイズは 1 バイトパディング）
    offset = body + size + (size & 1);
  }
  return 'incomplete';
}

/**
 * バケツ詰め本体。フレーム（= 全チャンネル分のサンプル）境界で分割しながら、
 * チャンクをまたぐ端数バイトを保持して逐次処理する。
 */
class PeakBucketer {
  readonly peaks: Float32Array;
  private frameIndex = 0;
  private leftover: Uint8Array | null = null;

  constructor(
    private readonly totalFrames: number,
    private readonly framesPerBucket: number,
    private readonly channels: number,
    targetPeaks: number,
  ) {
    this.peaks = new Float32Array(targetPeaks);
  }

  get done(): boolean {
    return this.frameIndex >= this.totalFrames;
  }

  pushBytes(bytes: Uint8Array): void {
    if (this.done || bytes.length === 0) return;
    const blockAlign = this.channels * 2;
    let buf = bytes;
    if (this.leftover && this.leftover.length) {
      buf = concatBytes(this.leftover, bytes);
      this.leftover = null;
    }
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const fullFrames = Math.floor(buf.length / blockAlign);
    const lastBucket = this.peaks.length - 1;
    for (let f = 0; f < fullFrames && this.frameIndex < this.totalFrames; f++) {
      const base = f * blockAlign;
      let maxAbs = 0;
      for (let ch = 0; ch < this.channels; ch++) {
        const a = Math.abs(dv.getInt16(base + ch * 2, true));
        if (a > maxAbs) maxAbs = a;
      }
      const bucket = Math.min(
        lastBucket,
        Math.floor(this.frameIndex / this.framesPerBucket),
      );
      const v = maxAbs / INT16_FULL_SCALE;
      if (v > this.peaks[bucket]) this.peaks[bucket] = v;
      this.frameIndex++;
    }
    const consumed = fullFrames * blockAlign;
    if (consumed < buf.length) {
      this.leftover = buf.slice(consumed);
    }
  }
}

function buildResult(
  format: WavFormatInfo,
  targetPeaksMax: number,
): { bucketer: PeakBucketer; durationSec: number } {
  if (format.bitsPerSample !== 16) {
    throw new Error(`Unsupported WAV bitsPerSample: ${format.bitsPerSample}`);
  }
  if (
    !(format.sampleRate > 0) ||
    format.dataSize <= 0 ||
    format.dataSize === 0xffffffff
  ) {
    throw new Error('Invalid WAV data/sample-rate info');
  }
  const channels = Math.max(1, format.channels);
  const blockAlign = channels * 2;
  const totalFrames = Math.floor(format.dataSize / blockAlign);
  if (totalFrames <= 0) {
    throw new Error('WAV contains no audio frames');
  }
  const durationSec = totalFrames / format.sampleRate;
  const targetPeaks = Math.max(1, Math.min(targetPeaksMax, totalFrames));
  const framesPerBucket = totalFrames / targetPeaks;
  const bucketer = new PeakBucketer(
    totalFrames,
    framesPerBucket,
    channels,
    targetPeaks,
  );
  return { bucketer, durationSec };
}

/**
 * `ReadableStreamDefaultReader` から生PCM WAV を読み進めつつピーク包絡を算出する。
 */
export async function streamWavPeaks(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: StreamWavPeaksOptions = {},
): Promise<WavPeaksResult> {
  const targetPeaksMax = options.targetPeaks ?? DEFAULT_TARGET_PEAKS;
  const signal = options.signal;
  const throwIfAborted = () => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  };
  const reportProgress = (received: number) => {
    options.onProgress?.(
      options.contentLength && options.contentLength > 0
        ? Math.round((received / options.contentLength) * 100)
        : null,
    );
  };

  // Phase 1: fmt/data を特定できるまでヘッダを蓄積
  let headerBuf = new Uint8Array(0);
  let format: WavFormatInfo | null = null;
  let received = 0;

  while (!format) {
    throwIfAborted();
    const { done, value } = await reader.read();
    if (done) break;
    if (!value || value.length === 0) continue;
    received += value.length;
    reportProgress(received);
    headerBuf = concatBytes(headerBuf, value);
    const parsed = parseWavHeader(headerBuf);
    if (parsed === null) {
      throw new Error('Unsupported or invalid WAV header');
    }
    if (parsed !== 'incomplete') {
      format = parsed;
      break;
    }
    if (headerBuf.length > HEADER_SCAN_LIMIT_BYTES) {
      throw new Error('WAV data chunk not found within scan limit');
    }
  }
  if (!format) {
    throw new Error('Stream ended before the WAV header could be parsed');
  }

  const { bucketer, durationSec } = buildResult(format, targetPeaksMax);

  // Phase 2: data 区間をバケツ詰め（ヘッダ蓄積分に含まれる data 先頭バイトも処理）
  if (headerBuf.length > format.dataOffset) {
    bucketer.pushBytes(headerBuf.subarray(format.dataOffset));
  }
  headerBuf = new Uint8Array(0); // 解放

  while (!bucketer.done) {
    throwIfAborted();
    const { done, value } = await reader.read();
    if (done) break;
    if (!value || value.length === 0) continue;
    received += value.length;
    reportProgress(received);
    bucketer.pushBytes(value);
  }

  return { peaks: bucketer.peaks, durationSec, sampleRate: format.sampleRate };
}

/**
 * 完全な ArrayBuffer から同様にピーク包絡を算出する（body ストリームが
 * 取得できない環境のフォールバック・単体テスト用）。
 */
export function computeWavPeaksFromBuffer(
  bytes: Uint8Array,
  targetPeaks: number = DEFAULT_TARGET_PEAKS,
): WavPeaksResult {
  const parsed = parseWavHeader(bytes);
  if (parsed === null || parsed === 'incomplete') {
    throw new Error('Unable to parse WAV header from buffer');
  }
  const { bucketer, durationSec } = buildResult(parsed, targetPeaks);
  const end = Math.min(bytes.length, parsed.dataOffset + parsed.dataSize);
  if (end > parsed.dataOffset) {
    bucketer.pushBytes(bytes.subarray(parsed.dataOffset, end));
  }
  return { peaks: bucketer.peaks, durationSec, sampleRate: parsed.sampleRate };
}
